// Per-player jump statistics aggregator, ported from the sibling vert-sync
// project's VertStatsObject.js (initWithJumpObjects) for Catapult Focus data:
// heights come from ev.imaHeightM (metres, synced onto match events by
// enrichMatchWithCatapultData) instead of a Vert belt's raw jump feed. A
// Focus export carries no landing-impact (G-force) data, so vert-sync's
// Landing Impact rows are replaced here with "Hang Time" (ev.imaDurationSec,
// the jump's hang time in seconds).
import {
    kSkillBlock,
    kSkillServe,
    kSkillSet,
    kSkillSettersCall,
    kSkillSpike,
} from "./Constants";

function skillKeyForEvent(ev) {
  return ev.EventType === kSkillSettersCall ? kSkillSet : ev.EventType;
}

function standardDeviation(arr) {
  if (arr.length === 0) return 0;
  const mean = arr.reduce((a, b) => a + b, 0) / arr.length;
  const variance = arr.reduce((a, b) => a + (b - mean) ** 2, 0) / arr.length;
  return Math.sqrt(variance);
}

// A set action under ~10cm is almost certainly a standing set, not a jump
// set -- same ~4in threshold vert-sync used (VertStatsObject.js `setjump`).
const kSetJumpThresholdCm = 10;

// `actionEvents`: this player's match events in scope (a set, or the whole
// match), any skill -- counts every Set action (jumping or standing) and
// pulls imaHeightM/DVGrade from the ones with synced jump data.
// `allJumps`: this player's raw Catapult jumps (group.jumps) in the same
// time window, action-matched or not -- the "ALL JUMPS" baseline, mirroring
// vert-sync's distinction between every jump the sensor recorded and only
// the ones tied to a scouted action ("ACTION JUMPS").
export function buildCatapultJumpStats(actionEvents, allJumps) {
  const self = {
    spikeJumpTotal: 0,
    spikeJumpKillTotal: 0,
    spikeJumpErrorTotal: 0,
    spikeJumpBlockedTotal: 0,
    spikeCountTotal: 0,
    spikeCountKillTotal: 0,
    spikeCountErrorTotal: 0,
    spikeCountBlockedTotal: 0,
    spikeJumpMaximum: 0,
    spikeJumpStandardDeviation: 0,
    spikeHangTimeTotal: 0,
    spikeHangTimeCountTotal: 0,
    blockJumpTotal: 0,
    blockJumpKillTotal: 0,
    blockJumpMaximum: 0,
    blockCountTotal: 0,
    blockCountKillTotal: 0,
    blockJumpStandardDeviation: 0,
    blockHangTimeTotal: 0,
    blockHangTimeCountTotal: 0,
    serveJumpTotal: 0,
    serveJumpKillTotal: 0,
    serveJumpErrorTotal: 0,
    serveJumpMaximum: 0,
    serveCountTotal: 0,
    serveCountKillTotal: 0,
    serveCountErrorTotal: 0,
    serveJumpStandardDeviation: 0,
    serveHangTimeTotal: 0,
    serveHangTimeCountTotal: 0,
    setJumpTotal: 0,
    setJumpMaximum: 0,
    setJumpCountTotal: 0,
    setCountTotal: 0,
    setJumpStandardDeviation: 0,
    setHangTimeTotal: 0,
    setHangTimeCountTotal: 0,
    allJumpActionTotal: 0,
    allJumpActionCountTotal: 0,
    allJumpActionMax: 0,
    allJumpTotal: 0,
    allJumpCountTotal: 0,
    allJumpMax: 0,
  };

  for (const j of allJumps ?? []) {
    if (j.heightM == null) continue;
    const cm = j.heightM * 100;
    self.allJumpMax = Math.max(self.allJumpMax, cm);
    self.allJumpTotal += cm;
    self.allJumpCountTotal++;
  }

  const aspikes = [];
  const ablocks = [];
  const aserves = [];
  const asets = [];
  for (const ev of actionEvents ?? []) {
    const skill = skillKeyForEvent(ev);
    if (skill === kSkillSet) self.setCountTotal++;

    if (ev.imaHeightM == null) continue;
    const cm = ev.imaHeightM * 100;
    self.allJumpActionTotal += cm;
    self.allJumpActionCountTotal++;
    self.allJumpActionMax = Math.max(self.allJumpActionMax, cm);

    const grade = ev.DVGrade;
    const hangTime = ev.imaDurationSec;
    if (skill === kSkillSpike) {
      aspikes.push(cm);
      self.spikeCountTotal++;
      self.spikeJumpTotal += cm;
      self.spikeJumpMaximum = Math.max(self.spikeJumpMaximum, cm);
      if (hangTime != null) {
        self.spikeHangTimeTotal += hangTime;
        self.spikeHangTimeCountTotal++;
      }
      if (grade === "#") {
        self.spikeCountKillTotal++;
        self.spikeJumpKillTotal += cm;
      } else if (grade === "=") {
        self.spikeCountErrorTotal++;
        self.spikeJumpErrorTotal += cm;
      } else if (grade === "/") {
        self.spikeCountBlockedTotal++;
        self.spikeJumpBlockedTotal += cm;
      }
    } else if (skill === kSkillBlock) {
      ablocks.push(cm);
      self.blockCountTotal++;
      self.blockJumpTotal += cm;
      self.blockJumpMaximum = Math.max(self.blockJumpMaximum, cm);
      if (hangTime != null) {
        self.blockHangTimeTotal += hangTime;
        self.blockHangTimeCountTotal++;
      }
      if (grade === "#") {
        self.blockCountKillTotal++;
        self.blockJumpKillTotal += cm;
      }
    } else if (skill === kSkillServe) {
      aserves.push(cm);
      self.serveCountTotal++;
      self.serveJumpTotal += cm;
      self.serveJumpMaximum = Math.max(self.serveJumpMaximum, cm);
      if (hangTime != null) {
        self.serveHangTimeTotal += hangTime;
        self.serveHangTimeCountTotal++;
      }
      if (grade === "#") {
        self.serveCountKillTotal++;
        self.serveJumpKillTotal += cm;
      } else if (grade === "=") {
        self.serveCountErrorTotal++;
        self.serveJumpErrorTotal += cm;
      }
    } else if (skill === kSkillSet) {
      self.setJumpTotal += cm;
      self.setJumpMaximum = Math.max(self.setJumpMaximum, cm);
      if (hangTime != null) {
        self.setHangTimeTotal += hangTime;
        self.setHangTimeCountTotal++;
      }
      if (cm > kSetJumpThresholdCm) {
        asets.push(cm);
        self.setJumpCountTotal++;
      }
    }
  }

  self.spikeJumpStandardDeviation = standardDeviation(aspikes);
  self.blockJumpStandardDeviation = standardDeviation(ablocks);
  self.serveJumpStandardDeviation = standardDeviation(aserves);
  self.setJumpStandardDeviation = standardDeviation(asets);
  return self;
}
